// CertificadosTablaSection.js — Sección `certificados_tabla`: los certificados
// del proyecto en una tabla (docs/Lac/certificado.png).
//
// Nombre · Progreso · Estado · Acciones. Mientras falten requisitos sale
// «Pendiente»; al cumplirlos, los botones para ver y descargar el PDF. Usa el
// sistema de certificados de la plataforma (certificados-v2.php), así que lo
// que se emite, el diseño y los requisitos se administran donde siempre
// (ficha del proyecto → Certificado).
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { certificadoService, encuestaService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const Seccion = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  font-family: Arial, Helvetica, sans-serif;
  @media (max-width: 767px) { padding: 36px 0 44px; }
`;

const Tarjeta = styled.div`
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  box-sizing: border-box;
  background: #fff;
  border-radius: 23px;
  padding: 42px 42px 60px;
  @media (max-width: 767px) { padding: 22px 16px 28px; }
`;

const Titulo = styled.h2`
  margin: 0 0 22px;
  padding: 0;
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${p => p.$color};
`;

const Scroll = styled.div`
  overflow-x: auto;
`;

const Tabla = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e5e7eb;
  font-size: 16px;
  color: #6b7280;
  th {
    text-align: left;
    padding: 18px 20px;
    border: 1px solid #e5e7eb;
    background: #fbfbfc;
    color: ${p => p.$colorTitulo};
    font-family: 'Roboto', Helvetica, Arial, sans-serif;
    font-weight: 700;
    white-space: nowrap;
  }
  td {
    padding: 18px 20px;
    border: 1px solid #e5e7eb;
    vertical-align: middle;
    line-height: 1.35;
  }
  /* Hasta 991px la tabla pasa a tarjetas: una por certificado, con el nombre
     arriba y cada dato con su rótulo (sale de data-label). */
  @media (max-width: 991px) {
    border: none;
    thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
    tbody, tr, td { display: block; }
    tr {
      border: 1px solid #e5e7eb;
      border-radius: 14px;
      padding: 4px 18px;
      background: #fff;
    }
    tr + tr { margin-top: 14px; }
    td {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 13px 0;
      border: none;
      border-top: 1px solid #f0f1f3;
      text-align: right;
    }
    td::before {
      content: attr(data-label);
      flex: none;
      color: ${p => p.$colorTitulo};
      font-family: 'Roboto', Helvetica, Arial, sans-serif;
      font-weight: 700;
      text-align: left;
    }
    td.nombre {
      display: block;
      border-top: none;
      padding: 16px 0 12px;
      text-align: left;
      color: ${p => p.$colorTitulo};
      font-family: 'Roboto', Helvetica, Arial, sans-serif;
      font-size: 17px;
      font-weight: 700;
    }
    td.nombre::before { content: none; }
  }
  @media (max-width: 480px) {
    font-size: 15px;
    tr { padding: 4px 14px; }
    td.con-botones { flex-direction: column; align-items: stretch; gap: 10px; }
    td.con-botones > div { width: 100%; }
    td.con-botones button { flex: 1; justify-content: center; }
  }
`;

const Chip = styled.span`
  display: inline-block;
  min-width: 78px;
  padding: 6px 10px;
  border-radius: 4px;
  background: ${p => (p.$hecho ? `${p.$color}1a` : '#f1f2f4')};
  color: ${p => (p.$hecho ? p.$color : '#6b7280')};
  font-size: 14px;
  font-weight: ${p => (p.$hecho ? 700 : 400)};
  text-align: center;
`;

/* Completado en el color de la web; pendiente, en rojo. */
const Estado = styled.span`
  color: ${p => p.$color};
  font-weight: ${p => (p.$hecho ? 700 : 400)};
`;

const Pendiente = styled.span`
  color: ${p => p.$color};
  font-weight: 700;
`;

const Acciones = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Boton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border: none;
  border-radius: 8px;
  background: ${p => p.$fondo};
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  &:hover { filter: brightness(1.08); }
  &:disabled { opacity: .6; cursor: default; }
  ${p => p.$claro && `background: #fff; color: ${p.$fondo}; border: 1px solid ${p.$fondo};`}
`;

const Aviso = styled.p`
  margin: 18px 0 0;
  color: #6b7280;
  font-size: 16px;
  a, button { color: ${p => p.$color}; font-weight: 700; }
  button { border: none; background: none; cursor: pointer; font-size: 16px; padding: 0; margin-left: 6px; }
`;

const CertificadosTablaSection = ({ config = {}, proyecto, openLogin, tl = (v, d = '') => v || d }) => {
  const c = config;
  const { user } = useAuth() || {};
  const acento = c.color_acento || '#8800cc';
  // «Completado» y el 100% van en el color de la web.
  const colorCompletado = c.color_completado || acento;
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState('');

  const cargar = useCallback(() => {
    if (!user || !proyecto?.id) return undefined;
    return certificadoService.miProgreso(proyecto.id)
      .then(r => setDatos({ emitidos: r.data?.emitidos || [], en_progreso: r.data?.en_progreso || [] }))
      .catch(() => setError('No se pudieron cargar los certificados.'));
  }, [user, proyecto?.id]);

  useEffect(() => { cargar(); }, [cargar]);

  // Certificados de ESTE proyecto (la plataforma devuelve los de todos).
  const mios = [
    ...(datos?.emitidos || []).filter(x => String(x.proyecto_id) === String(proyecto?.id)),
    ...(datos?.en_progreso || []).filter(x => String(x.proyecto_id) === String(proyecto?.id)),
  ];

  // Antes de ver o descargar: si el certificado pide encuesta, se responde en
  // la pantalla de certificados de la plataforma.
  const conEncuesta = async (item, seguir) => {
    try {
      const r = await encuestaService.estado(item.proyecto_id, item.certificado_key || 'default');
      if (r.data?.requiere) { window.location.assign('/certificados'); return; }
    } catch (e) { /* si falla la comprobación, no se bloquea */ }
    seguir();
  };

  const abrirPdf = (item, descargar) => conEncuesta(item, async () => {
    setOcupado(`${item.certificado_id}-${descargar ? 'd' : 'v'}`);
    try {
      const res = await certificadoService.descargarPDF(item.certificado_id);
      const url = URL.createObjectURL(res.data);
      if (descargar) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(item.titulo || 'certificado').replace(/[^\w\s-]+/g, '')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError('No se pudo abrir el certificado.');
    } finally {
      setOcupado('');
    }
  });

  const textoEstado = (item) => {
    if (item.estado === 'emitido') {
      const fecha = item.fecha ? new Date(item.fecha.replace(' ', 'T')).toLocaleDateString('es-CO') : '';
      return tl(c.texto_emitido, 'Completado').replace('{fecha}', fecha).trim();
    }
    return tl(c.texto_progreso, 'Progreso: {hechos} de {total} cursos completados')
      .replace('{hechos}', item.cumplidos ?? 0)
      .replace('{total}', item.total ?? 0);
  };

  return (
    <Seccion $fondo={c.fondo || '#f8f5ff'} $padding={c.padding || '60px 0 70px'}>
      <Tarjeta $ancho={c.ancho_maximo || '1000px'}>
        {tl(c.titulo, '') && <Titulo $color={c.color_titulo || '#0f2a42'}>{tl(c.titulo, '')}</Titulo>}

        {!user && (
          <Aviso $color={acento}>
            {tl(c.texto_sin_sesion, 'Inicia sesión para ver tus certificados.')}
            {openLogin && <button type="button" onClick={() => openLogin({ destino: window.location.pathname })}>Iniciar sesión</button>}
          </Aviso>
        )}
        {user && !datos && !error && <Aviso $color={acento}>Cargando…</Aviso>}
        {error && <Aviso $color={acento} role="alert">{error}</Aviso>}

        {user && datos && mios.length === 0 && (
          <Aviso $color={acento}>{tl(c.texto_vacio, 'Todavía no hay certificados disponibles para tu cuenta.')}</Aviso>
        )}

        {mios.length > 0 && (
          <Scroll>
            <Tabla $colorTitulo={c.color_titulo || '#0f2a42'}>
              <thead>
                <tr>
                  <th>{tl(c.col_nombre, 'Nombre')}</th>
                  <th>{tl(c.col_progreso, 'Progreso')}</th>
                  <th>{tl(c.col_estado, 'Estado')}</th>
                  <th>{tl(c.col_acciones, 'Acciones')}</th>
                </tr>
              </thead>
              <tbody>
                {mios.map(item => (
                  <tr key={`${item.proyecto_id}-${item.certificado_key}`}>
                    <td className="nombre">{item.curso || item.titulo}</td>
                    <td data-label={tl(c.col_progreso, 'Progreso')}>
                      <Chip $hecho={item.estado === 'emitido'} $color={colorCompletado}>
                        {item.estado === 'emitido' ? '100%' : `${item.porcentaje ?? 0}%`}
                      </Chip>
                    </td>
                    <td data-label={tl(c.col_estado, 'Estado')}>
                      <Estado $hecho={item.estado === 'emitido'} $color={item.estado === 'emitido' ? colorCompletado : '#6b7280'}>
                        {textoEstado(item)}
                      </Estado>
                    </td>
                    <td className={item.estado === 'emitido' ? 'con-botones' : undefined} data-label={tl(c.col_acciones, 'Acciones')}>
                      {item.estado === 'emitido' ? (
                        <Acciones>
                          <Boton type="button" $fondo={acento} disabled={ocupado === `${item.certificado_id}-v`}
                            onClick={() => abrirPdf(item, false)}>
                            {tl(c.texto_ver, 'Ver')}
                          </Boton>
                          <Boton type="button" $fondo={acento} $claro disabled={ocupado === `${item.certificado_id}-d`}
                            onClick={() => abrirPdf(item, true)}>
                            {tl(c.texto_descargar, 'Descargar')}
                          </Boton>
                        </Acciones>
                      ) : (
                        <Pendiente $color={c.color_pendiente || '#d32f2f'}>{tl(c.texto_pendiente, 'Pendiente')}</Pendiente>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          </Scroll>
        )}

        {c.url_curso && mios.some(x => x.estado !== 'emitido') && (
          <Aviso $color={acento}>
            <Link to={c.url_curso}>{tl(c.texto_ir_curso, 'Ir al curso')}</Link>
          </Aviso>
        )}
      </Tarjeta>
    </Seccion>
  );
};

export default CertificadosTablaSection;
